import {
  ExcelFileMetadata,
  ReceiptUploadStatus,
  UploadStatus,
} from 'store/organisation/models';
import {
  isUploadStatusProcessing,
  parseUploadStatus,
} from 'utils/uploadStatusHelpers';

/**
 * Tracks a rescan triggered from the transaction review drawer. The drawer
 * closes immediately; once the new scan is ready we reopen it for the first
 * document in the transactions side panel (most recent upload on top).
 */
export type PendingRescanDrawerReopen = {
  /** Set once the new scan is ready and the drawer should be reopened. */
  ready: boolean;
  /** Batch id observed when the rescan was registered (the pre-rescan batch). */
  initialBatchId?: string | null;
  /** First batch id seen after registration — the batch the rescan kicked off. */
  activeBatchId?: string;
  /** True once we've observed any file move through processing. */
  sawProcessing?: boolean;
  /** Set when the receipts batch SSE reports DONE (scan finished server-side). */
  batchComplete?: boolean;
  /**
   * Snapshot of the side-panel files before the rescan (id + uploadDate +
   * status + rows). Reopen waits until this signature changes — guaranteeing
   * the refreshed list (with the freshly scanned document) has actually landed
   * before we pick the top file to open.
   */
  preRescanSignature?: string;
};

/** True when any file is mid-scan (WAITING_FOR_AI / BUILDING_TRANSACTIONS). */
export function hasProcessingSidePanelFile(
  files: ReadonlyArray<ExcelFileMetadata>
): boolean {
  return files.some((file) => {
    const parsed = parseUploadStatus(file.status);
    return parsed != null && isUploadStatusProcessing(parsed);
  });
}

/** Stable signature of the side-panel files; changes when the list refreshes. */
export function computeSidePanelSignature(
  files: ReadonlyArray<ExcelFileMetadata>
): string {
  return JSON.stringify(
    [...files]
      .map((file) => ({
        id: String(file.excelMetadataId ?? '').trim(),
        uploadDate: file.uploadDate ?? null,
        status: file.status ?? null,
        totalRows: file.totalRows ?? null,
        isReviewed:
          file.isReviewed === true ||
          (file as { isReviwed?: boolean }).isReviwed === true,
        isPosted: file.isPosted === true,
      }))
      .sort((a, b) => a.id.localeCompare(b.id))
  );
}

export const pendingRescanDrawerReopenRef: {
  current: PendingRescanDrawerReopen | null;
} = {
  current: null,
};

export type RescanDrawerReopenCompletion =
  | 'waiting'
  | 'processing'
  | 'complete'
  | 'error';

export type SidePanelFileTab = 'CAPTURED' | 'REVIEWED' | 'POSTED';

/** Same tab filter as the transactions file side panel (without search). */
export function filterSidePanelFiles(
  files: ReadonlyArray<ExcelFileMetadata>,
  options: {
    tab: SidePanelFileTab;
    isReceiptsOnlyProvider: boolean;
    isExcelProvider: boolean;
  }
): ExcelFileMetadata[] {
  const { tab, isReceiptsOnlyProvider, isExcelProvider } = options;
  if (isExcelProvider) return [...files];

  return files.filter((file) => {
    const isReviewed =
      file.isReviewed === true ||
      (file as { isReviwed?: boolean }).isReviwed === true;

    if (isReceiptsOnlyProvider) {
      return tab === 'REVIEWED' ? isReviewed : !isReviewed;
    }

    if (tab === 'POSTED') {
      return file.isPosted === true;
    }

    if (file.isPosted === true) {
      return false;
    }

    return tab === 'REVIEWED' ? isReviewed : !isReviewed;
  });
}

/** First file in the side panel list (most recent uploadDate first). */
export function resolveFirstSidePanelFileId(
  files: ReadonlyArray<ExcelFileMetadata>,
  options: {
    tab: SidePanelFileTab;
    isReceiptsOnlyProvider: boolean;
    isExcelProvider: boolean;
  }
): string | null {
  const filtered = filterSidePanelFiles(files, options);
  const sorted = [...filtered].sort(
    (a, b) => (Number(b.uploadDate) || 0) - (Number(a.uploadDate) || 0)
  );
  const first =
    sorted.find((file) => {
      const status = parseUploadStatus(file.status);
      return status === UploadStatus.ACTIVE && (file.totalRows ?? 0) > 0;
    }) ?? sorted[0];
  const fileId = String(first?.excelMetadataId ?? '').trim();
  return fileId || null;
}

/** Record that the active receipts batch finished (SSE BATCH DONE). */
export function markPendingRescanBatchComplete(): void {
  const pending = pendingRescanDrawerReopenRef.current;
  if (!pending || pending.ready) return;
  pendingRescanDrawerReopenRef.current = { ...pending, batchComplete: true };
}

/** Pull the top-level batch status string out of the (loosely typed) selector value. */
export function resolveReceiptBatchStatus(
  lastReceiptUploadStatus: unknown
): string | undefined {
  if (!lastReceiptUploadStatus || typeof lastReceiptUploadStatus !== 'object') {
    return undefined;
  }

  const wrapped = lastReceiptUploadStatus as {
    status?: ReceiptUploadStatus | string;
  };

  if (wrapped.status && typeof wrapped.status === 'object') {
    const inner = wrapped.status as ReceiptUploadStatus;
    return inner.status ? String(inner.status) : undefined;
  }

  if (typeof wrapped.status === 'string') {
    return wrapped.status;
  }

  return undefined;
}

export function syncPendingRescanActiveBatchId(
  pending: PendingRescanDrawerReopen,
  currentBatchId?: string | null
): PendingRescanDrawerReopen {
  const normalized = String(currentBatchId ?? '').trim();
  if (!normalized) return pending;
  if (pending.activeBatchId) return pending;
  if (pending.initialBatchId && normalized === pending.initialBatchId) {
    return pending;
  }
  return { ...pending, activeBatchId: normalized };
}

/** Ready when the batch finished and we can resolve a side-panel file to open. */
export function isRescanDrawerReopenComplete(params: {
  pending: PendingRescanDrawerReopen;
  metadata?: ReadonlyArray<ExcelFileMetadata>;
  batchStatus?: string;
  currentBatchId?: string | null;
  isReceiptsOnlyProvider?: boolean;
  isExcelProvider?: boolean;
}): RescanDrawerReopenCompletion {
  const {
    pending,
    metadata,
    batchStatus,
    currentBatchId,
    isReceiptsOnlyProvider = false,
    isExcelProvider = false,
  } = params;

  const batchDone =
    typeof batchStatus === 'string' &&
    batchStatus.toUpperCase() === 'DONE' &&
    (!pending.activeBatchId ||
      !currentBatchId ||
      pending.activeBatchId === String(currentBatchId).trim());

  const anyProcessing = (metadata ?? []).some((file) => {
    const parsed = parseUploadStatus(file.status);
    return parsed != null && isUploadStatusProcessing(parsed);
  });

  const scanFinished =
    pending.sawProcessing || batchDone || pending.batchComplete === true;
  if (!scanFinished || anyProcessing) {
    return anyProcessing ? 'processing' : 'waiting';
  }

  // Wait until the side panel actually refreshed (signature changed) so we open
  // the freshly scanned document rather than the previous top file.
  const signatureChanged =
    pending.preRescanSignature == null ||
    computeSidePanelSignature(metadata ?? []) !== pending.preRescanSignature;
  if (!signatureChanged) {
    return 'waiting';
  }

  const reopenFileId = resolveFirstSidePanelFileId(metadata ?? [], {
    tab: 'CAPTURED',
    isReceiptsOnlyProvider,
    isExcelProvider,
  });
  return reopenFileId ? 'complete' : 'waiting';
}
